import type { MemberKind } from '../ports/auth-port.ts';
import type { Visibility } from '../domain/entities/common.ts';
import { levelAtLeast, type Flag, type Level, type Module } from './modules.ts';
import type { RolePermissions } from './roles.ts';

/** Everything a decision needs about who is asking. */
export interface Principal {
  readonly uid: string;
  readonly companyId: string;
  readonly kind: MemberKind;
  readonly status: 'invited' | 'active' | 'inactive';
  readonly jobAccess: 'all' | 'assigned';
  readonly jobIds: readonly string[];
  readonly permissions: RolePermissions;
}

/** The thing being reached for, when it is a specific record. */
export interface Target {
  readonly companyId: string;
  readonly jobId?: string;
  readonly visibility?: Visibility;
  readonly createdBy?: string;
}

export interface Decision {
  readonly allowed: boolean;
  /** Why it was refused, for logging and for a useful error message. */
  readonly reason?:
    | 'inactive-member'
    | 'wrong-company'
    | 'job-not-assigned'
    | 'insufficient-level'
    | 'not-visible-to-kind'
    | 'private-to-creator';
}

const ALLOWED: Decision = { allowed: true };
const deny = (reason: NonNullable<Decision['reason']>): Decision => ({ allowed: false, reason });

/**
 * The authorisation decision, used everywhere.
 *
 * Checks run cheapest-and-broadest first, and every one of them is a hard
 * stop: tenancy, then membership standing, then job assignment, then module
 * level, then record visibility. A caller cannot skip a step by passing a
 * richer target, because each gate returns on failure rather than scoring.
 *
 * This runs in three places — the browser to hide controls, Cloud Functions to
 * reject writes, and (transliterated) Firestore rules to deny reads. The
 * browser copy is a convenience. The other two are the actual control.
 */
export function can(
  principal: Principal,
  module: Module,
  required: Level,
  target?: Target,
): Decision {
  if (principal.status !== 'active') return deny('inactive-member');

  if (target && target.companyId !== principal.companyId) return deny('wrong-company');

  if (target?.jobId && !hasJobAccess(principal, target.jobId)) return deny('job-not-assigned');

  const granted = principal.permissions.modules[module] ?? 'none';
  if (!levelAtLeast(granted, required)) return deny('insufficient-level');

  if (target?.visibility && !isVisibleTo(principal, target)) {
    return target.visibility.private ? deny('private-to-creator') : deny('not-visible-to-kind');
  }

  return ALLOWED;
}

/** Convenience wrapper for call sites that only need a boolean. */
export function allowed(
  principal: Principal,
  module: Module,
  required: Level,
  target?: Target,
): boolean {
  return can(principal, module, required, target).allowed;
}

export function hasFlag(principal: Principal, flag: Flag): boolean {
  return principal.status === 'active' && principal.permissions.flags.includes(flag);
}

export function hasJobAccess(principal: Principal, jobId: string): boolean {
  if (principal.jobAccess === 'all') return true;
  return principal.jobIds.includes(jobId);
}

/**
 * Whether a record's audience includes this principal.
 *
 * `private` wins over every other audience, including for an administrator:
 * the point of a private note is that it is private. Deletion and transfer of
 * ownership are separate questions, handled by flags rather than by widening
 * read access.
 */
export function isVisibleTo(principal: Principal, target: Target): boolean {
  const v = target.visibility;
  if (!v) return true;
  if (v.private) return target.createdBy === principal.uid;

  switch (principal.kind) {
    case 'internal':
      return v.internal;
    case 'sub':
      return v.subs;
    case 'client':
      return v.owner;
  }
}

/**
 * Whether cost, markup and margin may be returned to this principal.
 *
 * External members never see them regardless of role configuration. An
 * administrator could otherwise build a client role with the flag set and
 * leak margin to the homeowner, which is a commercial problem rather than a
 * bug, so the rule is enforced here rather than left to configuration.
 */
export function canSeeBuilderCost(principal: Principal): boolean {
  if (principal.kind !== 'internal') return false;
  return hasFlag(principal, 'seeBuilderCost');
}
